import { AllGiftCardsModel, GiftCardModel } from "@aihCustomBlueprint/Services/GiftCardService";
import CustomApplicationState from "@aihCustomBlueprint/Store/CustomApplicationState";
import addGiftCard from "@aihCustomBlueprint/Store/Data/GiftCard/Handlers/AddGiftCard";
import deleteGiftCard from "@aihCustomBlueprint/Store/Data/GiftCard/Handlers/DeleteGiftCard";
import getAllGiftCards from "@aihCustomBlueprint/Store/Data/GiftCard/Handlers/GetAllGiftCards";
import validateGiftCard from "@aihCustomBlueprint/Store/Data/GiftCard/Handlers/ValidateGiftCard";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cloneDeep from "lodash/cloneDeep";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

export interface GiftCardStyles {
    fieldset?: InjectableCss;
    parentContainer?: InjectableCss;
    giftCardHeading?: TypographyPresentationProps;
    widgetGridContainer?: GridContainerProps;
    giftCardCodeGrid?: GridItemProps;
    giftCardPinGrid?: GridItemProps;
    checkButtonGrid?: GridItemProps;
    availableAmountGrid?: GridItemProps;
    availableBalance?: InjectableCss;
    errorMessageGrid?: GridItemProps;
    errorMessage?: InjectableCss;
    amountGrid?: GridItemProps;
    applyGrid?: GridItemProps;
    removeGrid?: GridItemProps;
    textField?: TextFieldPresentationProps;
    checkButton?: ButtonPresentationProps;
    applyButton?: ButtonPresentationProps;
    removeButton?: ButtonPresentationProps;
    addCardButton?: ButtonPresentationProps;
    addCardItem?: InjectableCss;
}

export const giftCardStyles: GiftCardStyles = {
    fieldset: {
        css: css`
            margin: 0;
            padding: 25px 0 0 0;
            border: 0;
        `,
    },
    parentContainer: {
        css: css`
            padding: 10px 0 15px 0;
        `,
    },
    giftCardHeading: { variant: "h5" },
    giftCardCodeGrid: {
        width: [6, 6, 4, 4, 4],
    },
    giftCardPinGrid: {
        width: [6, 6, 4, 4, 4],
    },
    checkButtonGrid: {
        width: [6, 6, 2, 2, 2],
        css: css`
            flex-direction: column;
            justify-content: flex-start;
        `,
    },
    availableAmountGrid: {
        width: [6, 6, 4, 4, 4],
    },
    availableBalance: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            margin: 0;
            padding: 0;
            border: 0;
            > label {
                display: inline-block;
                width: 100%;
                min-height: 22px;
            }
            > span {
                margin-top: 10px;
                width: 100%;
                font-weight: 700;
            }
        `,
    },
    errorMessageGrid: {
        width: [6, 6, 4, 4, 4],
    },
    errorMessage: {
        css: css`
            padding: 0.5em 1em;
            font-size: 0.9em;
            line-height: 1.5em;
            font-weight: 600;
            background: #fff;
            color: #c60f13;
            display: block;
            clear: both;
            font-size: 14px;
            margin-bottom: 0.25em;
            border: 1px solid #c60f13;
        `,
    },
    amountGrid: {
        width: [6, 6, 4, 4, 4],
    },
    applyGrid: {
        width: [6, 6, 2, 2, 2],
        css: css`
            flex-direction: column;
            justify-content: flex-start;
        `,
    },
    removeGrid: {
        width: [6, 6, 2, 2, 2],
        css: css`
            flex-direction: column;
            justify-content: flex-start;
        `,
    },
    checkButton: {
        css: css`
            margin-top: 32px;
            background: #c7131a;
            border: 1px solid #363636;
            :hover {
                background-color: #ed1c24;
                border: 1px solid #363636;
            }
        `,
    },
    applyButton: {
        css: css`
            margin-top: 32px;
            background: ${getColor("secondary.main")};
            border: 1px solid #363636;
            :hover {
                background-color: #9b9fa2;
                border: 1px solid #363636;
            }
        `,
    },
    removeButton: {
        css: css`
            margin-top: 32px;
            background: ${getColor("secondary.main")};
            border: 1px solid #363636;
            :hover {
                background-color: #9b9fa2;
                border: 1px solid #363636;
            }
        `,
    },
    addCardButton: {
        css: css`
            border: 1px solid #363636;
        `,
    },
    addCardItem: {
        css: css`
            padding: 10px 0;
        `,
    },
};

const mapStateToProps = (state: CustomApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    return {
        session: state.context.session,
        allGiftCards: state.AIHState.GiftCards.allGiftCards,
        isLoading: state.AIHState.GiftCards.isLoading,
        cart: cartState.value,
    };
};

const mapDispatchToProps = {
    validateGiftCard,
    addGiftCard,
    deleteGiftCard,
    getAllGiftCards,
    updateCart: makeHandlerChainAwaitable(updateCart),
};

export interface GiftCardList extends AllGiftCardsModel {
    showBalance?: boolean;
    applied?: boolean;
    giftCardCombinationError?: string;
    giftCardNumberError?: string;
    pinError?: string;
    appliedAmountError?: string;
}

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const StyledFieldSet = getStyledWrapper("fieldset");
const Label = styled.label`
    display: block;
    font-size: 15px;
    font-weight: bold;
    line-height: 21px;
    position: relative;
`;

export const GiftCard = ({
    cart,
    allGiftCards,
    isLoading,
    validateGiftCard,
    addGiftCard,
    deleteGiftCard,
    getAllGiftCards,
    updateCart,
}: Props) => {
    const newGridCard: GiftCardList = {
        giftCardNumber: "",
        appliedAmount: "",
        pin: "",
        balance: "",
        showBalance: false,
        applied: false,
    };
    const [giftCards, setGiftCards] = useState<GiftCardList[]>([newGridCard]);
    const [addAnotherCard, setAddAnotherCard] = useState<boolean>(false);

    React.useEffect(() => {
        getAllGiftCards({});
    }, []);

    React.useEffect(() => {
        if (allGiftCards && allGiftCards.length !== 0) {
            const myGiftCards: GiftCardList[] = [];
            allGiftCards.forEach(card => {
                const cardClone = cloneDeep(card) as GiftCardList;
                cardClone.showBalance = true;
                cardClone.applied = true;
                myGiftCards.push(cardClone);
            });
            setGiftCards(myGiftCards);
            setAddAnotherCard(true);
        }
    }, [allGiftCards]);

    const addNewGiftCard = () => {
        setGiftCards([...giftCards, newGridCard]);
        setAddAnotherCard(false);
    };

    const decimalOnly = (evt: any) => {
        const charCode = evt.which ? evt.which : evt.keyCode;
        if (charCode !== 46 && charCode > 31 && (charCode < 48 || charCode > 57)) {
            evt.preventDefault();
        }
        if (charCode === 46 && evt.target.value.indexOf(".") !== -1) {
            evt.preventDefault();
        }
    };

    const checkCard = (i: any) => {
        const giftCardList = [...giftCards];
        validateGiftCard({
            giftCardNumber: giftCardList[i].giftCardNumber,
            pin: giftCardList[i].pin,
            appliedAmount: giftCardList[i].appliedAmount,
            balance: giftCardList[i].balance,
            onSuccess: (result: GiftCardModel | null) => {
                if (result != null && result?.success) {
                    giftCardList[i].giftCardCombinationError = "";
                    giftCardList[i].showBalance = true;
                    if (result.data) {
                        giftCardList[i].balance = result.data.balance.toString();
                        if (cart && parseFloat(result.data.balance.toString()) > cart.orderGrandTotal) {
                            giftCardList[i].appliedAmount = cart.orderGrandTotal.toString();
                        } else {
                            giftCardList[i].appliedAmount = result.data.balance.toString();
                        }
                    }

                    setGiftCards(giftCardList);
                } else if (!result?.success) {
                    giftCardList[i].balance = "";
                    giftCardList[i].giftCardCombinationError = result?.msg ? result?.msg : "";
                    giftCardList[i].showBalance = false;
                }
            },
            onError: (msg: string | null | undefined) => {
                if (msg) {
                    giftCardList[i].balance = "";
                    giftCardList[i].giftCardCombinationError = msg;
                    giftCardList[i].showBalance = false;
                    setGiftCards(giftCardList);
                }
            },
        });
    };

    const applyCard = (i: any) => {
        const giftCardList = [...giftCards];
        addGiftCard({
            appliedAmount: giftCardList[i].appliedAmount,
            giftCardNumber: giftCardList[i].giftCardNumber,
            pin: giftCardList[i].pin,
            balance: giftCardList[i].balance,
            onSuccess: async (appliedMsg: string) => {
                giftCardList[i].applied = true;
                setAddAnotherCard(true);
                await updateCart({});
            },
            onError: (message: string) => {
                giftCardList[i].applied = false;
            },
        });
    };

    const removeCard = (i: any) => {
        const giftCardList = [...giftCards];
        deleteGiftCard({
            giftCardNumber: giftCardList[i].giftCardNumber,
            onSuccess: async (msg: string) => {
                giftCardList.splice(i, 1);
                if (giftCardList.length === 0) {
                    giftCardList.push(newGridCard);
                    setAddAnotherCard(false);
                }
                setGiftCards(giftCardList);

                await updateCart({});
            },
        });
    };

    const handleChange = (e: any, i: number) => {
        const { name, value } = e.target;
        const giftCardList = [...giftCards];
        if (name === "giftCardNumber") {
            giftCardList[i].giftCardNumber = value;
            if (value && value.length === 16) {
                giftCardList[i].giftCardNumberError = "";
            }
        }
        if (name === "appliedAmount") {
            giftCardList[i].appliedAmount = value;
            const card = giftCardList[i];
            if (
                (cart && parseFloat(card.appliedAmount) > cart.orderGrandTotal) ||
                parseFloat(card.appliedAmount) > parseFloat(card.balance.toString())
            ) {
                giftCardList[i].appliedAmountError =
                    !cart || parseFloat(card.balance.toString()) < cart.orderGrandTotal
                        ? "Amount should be less than available balance"
                        : "Amount should be less than order total";
            } else if (card.appliedAmount === "0") {
                giftCardList[i].appliedAmountError = "Amount should be more than $0";
            } else {
                giftCardList[i].appliedAmountError = "";
            }
        }
        if (name === "pin") {
            giftCardList[i].pin = value;
            if (value && value.length === 4) {
                giftCardList[i].pinError = "";
            }
        }
        if (name === "balance") {
            giftCardList[i].balance = value;
        }
        setGiftCards(giftCardList);
    };

    const validateData = (e: any, i: number) => {
        const { name, value } = e.target;
        const giftCardList = [...giftCards];
        if (name === "giftCardNumber") {
            if (!value || value.length < 16) {
                giftCardList[i].giftCardNumberError = "Please enter at least 16 characters.";
            } else {
                giftCardList[i].giftCardNumberError = "";
            }
        }

        if (name === "pin") {
            if (!value || value.length < 4) {
                giftCardList[i].pinError = "Please enter at least 4 characters.";
            } else {
                giftCardList[i].pinError = "";
            }
        }

        setGiftCards(giftCardList);
    };

    const styles = giftCardStyles;

    return (
        <StyledFieldSet {...styles.fieldset}>
            <Typography {...styles.giftCardHeading} as="h2">
                {translate("Gift Cards")}
            </Typography>

            {giftCards.map((card, i) => (
                <StyledWrapper {...styles.parentContainer} key={i}>
                    <GridContainer {...styles.widgetGridContainer}>
                        <GridItem {...styles.giftCardCodeGrid}>
                            <TextField
                                {...styles.textField}
                                label={<span aria-hidden>{translate("Gift Card Code")}</span>}
                                name="giftCardNumber"
                                value={card.giftCardNumber}
                                onChange={e => handleChange(e, i)}
                                onBlur={e => validateData(e, i)}
                                error={card.giftCardNumberError ? translate(card.giftCardNumberError) : null}
                                required={true}
                                maxLength={16}
                                minLength={16}
                                disabled={card.showBalance}
                                data-test-selector="checkoutReviewAndSubmit_cardCode"
                            />
                        </GridItem>
                        <GridItem {...styles.giftCardPinGrid}>
                            <TextField
                                {...styles.textField}
                                label={<span aria-hidden>{translate("Gift Card PIN")}</span>}
                                name="pin"
                                type="password"
                                value={card.pin}
                                onChange={e => handleChange(e, i)}
                                onBlur={e => validateData(e, i)}
                                required={true}
                                maxLength={4}
                                minLength={4}
                                disabled={card.showBalance}
                                error={card.pinError ? translate(card.pinError) : null}
                                data-test-selector="checkoutReviewAndSubmit_cardPin"
                            />
                        </GridItem>
                        <GridItem {...styles.checkButtonGrid}>
                            {!card.showBalance && (
                                <Button
                                    {...styles.checkButton}
                                    onClick={() => checkCard(i)}
                                    disabled={
                                        !card.pin ||
                                        card.pin.length < 4 ||
                                        !card.giftCardNumber ||
                                        card.giftCardNumber.length < 16
                                    }
                                    aria-label={translate("Check")}
                                    data-test-selector="checkBtn"
                                >
                                    {translate("Check")}
                                </Button>
                            )}
                        </GridItem>
                    </GridContainer>
                    {card.giftCardCombinationError && card.giftCardCombinationError.length > 0 ? (
                        <GridContainer {...styles.widgetGridContainer}>
                            <GridItem {...styles.errorMessageGrid}>
                                <Typography as="span" {...styles.errorMessage}>
                                    {translate(card.giftCardCombinationError)}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                    ) : (
                        <>
                            {card.showBalance && (
                                <GridContainer {...styles.widgetGridContainer}>
                                    <GridItem {...styles.availableAmountGrid}>
                                        <StyledWrapper {...styles.availableBalance}>
                                            <Label>{translate("Available Balance")}</Label>
                                            <Typography as="span"> {`$${card.balance}`}</Typography>
                                        </StyledWrapper>
                                    </GridItem>
                                    <GridItem {...styles.amountGrid}>
                                        <TextField
                                            {...styles.textField}
                                            label={<span aria-hidden>{translate("Amount to Apply to Order")}</span>}
                                            name="appliedAmount"
                                            value={card.appliedAmount}
                                            onChange={e => handleChange(e, i)}
                                            onKeyPress={e => decimalOnly(e)}
                                            required={true}
                                            disabled={card.applied}
                                            error={card.appliedAmountError ? translate(card.appliedAmountError) : null}
                                            data-test-selector="checkoutReviewAndSubmit_cardAmount"
                                        />
                                    </GridItem>

                                    <GridItem {...styles.applyGrid}>
                                        <Button
                                            {...styles.applyButton}
                                            onClick={() => applyCard(i)}
                                            disabled={
                                                card.applied ||
                                                !parseFloat(card.appliedAmount) ||
                                                !cart ||
                                                !cart.orderGrandTotal ||
                                                parseFloat(card.appliedAmount) > cart.orderGrandTotal ||
                                                parseFloat(card.appliedAmount) > parseFloat(card.balance.toString()) ||
                                                parseFloat(card.balance.toString()) === 0 ||
                                                card.appliedAmount === "0"
                                            }
                                            aria-label={card.applied ? translate("Applied") : translate("Apply")}
                                            data-test-selector="applyBtn"
                                        >
                                            {card.applied ? translate("Applied") : translate("Apply")}
                                        </Button>
                                    </GridItem>
                                    <GridItem {...styles.removeGrid}>
                                        <Button
                                            {...styles.removeButton}
                                            onClick={() => removeCard(i)}
                                            aria-label={translate("Remove")}
                                            disabled={!card.applied}
                                            data-test-selector="removeBtn"
                                        >
                                            {translate("Remove")}
                                        </Button>
                                    </GridItem>
                                </GridContainer>
                            )}
                        </>
                    )}
                </StyledWrapper>
            ))}

            <StyledWrapper {...styles.addCardItem}>
                {addAnotherCard && (
                    <Button
                        {...styles.addCardButton}
                        onClick={addNewGiftCard}
                        aria-label={translate("AddNewCard")}
                        data-test-selector="AddNewCardBtn"
                    >
                        {translate("Add Another Gift Card")}
                    </Button>
                )}
            </StyledWrapper>
        </StyledFieldSet>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(GiftCard),
    definition: {
        displayName: "Gift Card",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        group: "Checkout - Review & Submit",
    },
};

export default widgetModule;
